const reasons = ['abuse', 'privacy', 'advertising', 'inappropriate', 'other'];

const messageTimeFormatter = new Intl.DateTimeFormat('zh-CN', {
  timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

export function formatMessageTime(value) {
  const unknown = { dateTime: null, text: '—' };
  if (typeof value !== 'string') return unknown;
  const input = value.trim();
  // The existing API returns database UTC timestamps without a timezone suffix.
  // Explicit ISO offsets are already unambiguous and must not be shifted twice.
  const databaseUtc = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(input);
  const normalized = databaseUtc ? `${input.replace(' ', 'T')}Z` : input;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(normalized)) return unknown;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return unknown;
  if (databaseUtc && date.toISOString().slice(0, 19) !== normalized.slice(0, 19)) return unknown;
  const parts = Object.fromEntries(messageTimeFormatter.formatToParts(date).map(part => [part.type, part.value]));
  return {
    dateTime: date.toISOString(),
    text: `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`,
  };
}

function id(value) {
  if (!/^[1-9]\d*$/.test(String(value)) || !Number.isSafeInteger(Number(value))) throw new Error('invalid id');
  return String(value);
}

function text(value, maximum = 2000, nullable = false) {
  if (value == null && nullable) return null;
  if (typeof value !== 'string' || [...value].length > maximum) throw new Error('invalid text');
  return value.trim();
}

function request(app, method, path, body) {
  return app.request({
    method,
    url: `${app.forum.attribute('apiUrl') || '/api'}${path}`,
    ...(body === undefined ? {} : { body }),
  }).then(response => response.data);
}

export function searchUsers(app, filter) {
  return request(app, 'GET', `/yingtao/messages/users?filter=${encodeURIComponent(text(filter, 200))}`);
}

export function listConversations(app) {
  return request(app, 'GET', '/yingtao/conversations');
}

export function createConversation(app, recipientId) {
  return request(app, 'POST', '/yingtao/conversations', { recipient_id: Number(id(recipientId)) });
}

export function listMessages(app, conversationId, before) {
  const query = `?limit=50${before == null ? '' : `&before=${id(before)}`}`;
  return request(app, 'GET', `/yingtao/conversations/${id(conversationId)}/messages${query}`);
}

export function sendMessage(app, conversationId, body) {
  if (!text(body)) throw new Error('invalid body');
  return request(app, 'POST', `/yingtao/conversations/${id(conversationId)}/messages`, { body });
}

export function markRead(app, conversationId, messageId) {
  return request(app, 'POST', `/yingtao/conversations/${id(conversationId)}/read`, { message_id: Number(id(messageId)) });
}

export function setBlocked(app, conversationId, blocked) {
  if (typeof blocked !== 'boolean') throw new Error('invalid blocked');
  return request(app, 'POST', `/yingtao/conversations/${id(conversationId)}/block`, { blocked });
}

export function reportMessage(app, messageId, reason, description) {
  if (!reasons.includes(reason)) throw new Error('invalid reason');
  return request(app, 'POST', `/yingtao/messages/${id(messageId)}/reports`, {
    reason, description: text(description, 2000, true) || null,
  });
}

export function listReports(app, limit = 50, offset = 0) {
  if (!Number.isSafeInteger(limit) || limit < 1 || limit > 100 || !Number.isSafeInteger(offset) || offset < 0) throw new Error('invalid pagination');
  return request(app, 'GET', `/yingtao/message-reports?page%5Blimit%5D=${limit}&page%5Boffset%5D=${offset}`);
}

export function resolveReport(app, reportId, action, note) {
  if (!['close', 'delete_message'].includes(action)) throw new Error('invalid action');
  return request(app, 'POST', `/yingtao/message-reports/${id(reportId)}/resolve`, {
    action, note: text(note, 2000, true) || null,
  });
}

export function messageErrorKey(error) {
  const status = Number(error?.status || error?.response?.status);
  const codes = error?.response?.errors?.map(item => item.code) || [];
  if (error?.response?.errors?.some(item => String(item.detail || '').includes('已屏蔽'))) return 'blocked_error';
  if (status === 401) return 'login_required';
  if (status === 409 || codes.includes('yingtao_duplicate_message_report')) return 'duplicate_error';
  if (status === 403 || codes.includes('permission_denied')) return 'permission_error';
  if (status === 404 || codes.includes('not_found')) return 'not_found_error';
  if (status === 429 || codes.includes('flooding')) return 'rate_error';
  if (status === 422 || codes.includes('validation_error') || /invalid (body|text|id)/.test(error?.message || '')) return 'validation_error';
  return 'generic_error';
}
